import { formatMoney, type ItemOfferSummary, type Offer, type TripItemDealState } from '@sp/shared'
import { chainLabel, dealEndsNote, dealNote, offerPrice, offerTitle } from '../lib/offers.ts'
import { capitalize, inStore, plural } from '../lib/format.ts'
import { Icon } from './Icon.tsx'

/**
 * Jeden stitek akce u polozky vsude (navrh v3): v sablone, pri planovani,
 * v obchode i ve vyberu. deal = akce tady, quiet = jen jinde, applied = cena
 * uz je z akce, ended = akce skonci pred nakupem.
 */
export interface DealLabel {
  tone: 'deal' | 'quiet' | 'applied' | 'ended'
  text: string
}

const TONE_CLASS: Record<DealLabel['tone'], string> = {
  deal: 'offer-badge',
  quiet: 'offer-badge is-quiet',
  applied: 'offer-badge is-applied',
  ended: 'offer-badge is-ended',
}

const TONE_ICON = { deal: 'tag', quiet: 'tag', applied: 'check', ended: 'alert' } as const

/** Uvnitr tlacitka radku jen jako text (tlacitko v tlacitku neni platne HTML). */
export function DealTag({
  label,
  onClick,
  ariaLabel,
  className,
}: {
  label: DealLabel
  onClick?: (() => void) | undefined
  ariaLabel?: string | undefined
  className?: string | undefined
}) {
  const classes = [TONE_CLASS[label.tone], className].filter(Boolean).join(' ')
  const content = (
    <>
      <Icon name={TONE_ICON[label.tone]} size={14} strokeWidth={2} />
      <span>{label.text}</span>
    </>
  )
  if (!onClick) return <span className={classes}>{content}</span>
  return (
    <button type="button" className={classes} aria-label={ariaLabel} onClick={onClick}>
      {content}
    </button>
  )
}

/** „Akce 1,99 zł", „Akce od 0,99 €", s retezcem „Biedronka 2,39 zł". */
export function offerBadgeText(offer: Offer, from: boolean, withChain: boolean): string {
  const price = offerPrice(offer)
  const head = withChain ? chainLabel(offer.chain) : 'Akce'
  return price ? `${head} ${from ? 'od ' : ''}${price}` : head
}

/** Akce z letaku (stranka Akce, vyber ze sablony). */
export function OfferBadge({
  offer,
  onClick,
  from = false,
  withChain = false,
}: {
  offer: Offer
  onClick?: (() => void) | undefined
  from?: boolean
  withChain?: boolean
}) {
  return (
    <DealTag
      label={{ tone: 'deal', text: offerBadgeText(offer, from, withChain) }}
      onClick={onClick}
      ariaLabel={onClick ? `Akce: ${offerTitle(offer)}` : undefined}
    />
  )
}

/** Retezec bez upresneni zeme: „Lidl (Polsko)" -> „Lidl". */
function chainShort(chain: string): string {
  return chainLabel(chain).replace(/\s*\(.*\)$/, '')
}

/**
 * Stitek polozky sablony. V obchode polozky akce neni, jinde ano — bez ceny,
 * at eura z Nemecka nevypadaji jako cena v Tescu (spagety a omacka, 2026-09-16).
 */
export function templateDealLabel(summary: ItemOfferSummary<Offer>): DealLabel {
  if (summary.kind === 'elsewhere') {
    const [only] = summary.chains
    return {
      tone: 'quiet',
      text:
        summary.chains.length === 1 && only
          ? `Akce ${inStore(chainShort(only))}`
          : `Akce v ${plural(summary.chains.length, 'obchodě', 'obchodech', 'obchodech')}`,
    }
  }
  return { tone: 'deal', text: offerBadgeText(summary.offer, summary.count > 1, summary.kind === 'any') }
}

/** Stitek polozky nakupu podle tripItemDealState. */
export function tripDealLabel(state: TripItemDealState, storeName: (id: string | null) => string | null): DealLabel {
  if (state.kind === 'applied') {
    return state.ended
      ? { tone: 'ended', text: dealEndsNote(state.deal) }
      : { tone: 'applied', text: capitalize(dealNote(state.deal)) }
  }
  const { offer } = state.option
  if (state.kind === 'here') {
    if (offer.priceMinor === null) return { tone: 'deal', text: 'V akci' }
    return { tone: 'deal', text: `Akce ${state.count > 1 ? 'od ' : ''}${formatMoney(offer.priceMinor, offer.currency)}` }
  }
  const name = storeName(state.option.storeId)
  const where = name ? ` ${inStore(name)}` : ''
  return { tone: 'quiet', text: state.cheaper ? `Levněji${where}` : `Akce${where}` }
}
